import { gsap } from "gsap";

/**
 * Splits the static heading into per-line elements so each rendered line can
 * carry its own angled yellow panel.
 *
 * This has to happen at runtime: where the lines fall depends on the final
 * webfont, the viewport width and the container size, none of which are known
 * server-side. A single element around the whole sentence gets broken by the
 * browser anyway, leaving one rotated ::before stepping across mid-line — which
 * is the "only one shape for all the lines" symptom.
 *
 * Done by MEASUREMENT rather than GSAP's SplitText. That plugin was the
 * original approach, but `SplitText` was not making it into the built bundle
 * (`autoSplit` appeared in the output while the class itself did not), so
 * `SplitText.create` never ran, `is-split` was never applied, and the CSS fell
 * back to its single whole-heading panel every time.
 *
 * Walking the words with a Range and watching for a jump in `top` reads the
 * real layout, needs no plugin, and re-runs on font load and resize.
 */
function splitIntoLines(el) {
  // The untouched text is kept on the element so a re-split (on resize, where
  // the wrap points move) starts from the original rather than from markup this
  // function already produced.
  if (el.dataset.lineText === undefined) {
    el.dataset.lineText = el.textContent.trim();
  }

  const text = el.dataset.lineText;
  if (!text) return;

  // Reset to a single text node so the measuring pass sees the browser's
  // natural wrapping, not last run's one-line-per-element layout.
  el.classList.remove("is-split");
  el.textContent = text;

  const textNode = el.firstChild;
  if (!textNode) return;

  // Walk the words, recording where the browser moved to a new line. Reading
  // the real layout keeps this correct whatever the font, viewport or
  // container width turn out to be.
  const lines = [];
  let current = "";
  let previousTop = null;
  let offset = 0;
  const range = document.createRange();

  for (const word of text.split(/\s+/)) {
    if (!word) continue;

    const start = text.indexOf(word, offset);
    if (start === -1) continue;
    offset = start + word.length;

    range.setStart(textNode, start);
    range.setEnd(textNode, offset);

    const rect = range.getClientRects()[0];
    if (!rect) {
      current = current ? `${current} ${word}` : word;
      continue;
    }

    // A word whose top differs from the previous one has wrapped. The 2px
    // tolerance absorbs sub-pixel differences between words on the same line.
    if (previousTop !== null && Math.abs(rect.top - previousTop) > 2) {
      lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }

    previousTop = rect.top;
  }

  if (current) lines.push(current);
  range.detach?.();

  if (!lines.length) return;

  const fragment = document.createDocumentFragment();
  lines.forEach((line) => {
    const span = document.createElement("span");
    span.className = "block__text-cycle__static-line";
    span.textContent = line;
    fragment.appendChild(span);
  });

  el.textContent = "";
  el.appendChild(fragment);
  // Switches the CSS from the single fallback panel to the per-line ones.
  el.classList.add("is-split");
}

function initHighlightLines() {
  const headings = document.querySelectorAll("[data-cycle-highlight]");
  if (!headings.length) return;

  const splitAll = () => headings.forEach(splitIntoLines);

  // Fonts change text metrics, so wrap points measured before the webfont
  // lands would be wrong and the bars would visibly re-flow.
  if (document.fonts?.ready) {
    document.fonts.ready.then(splitAll);
  } else {
    splitAll();
  }

  // Wrap points move with the container width. Debounced — this measures
  // layout and would otherwise run on every frame of a drag.
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(splitAll, 200);
  });
}

/**
 * Text (cycle) — one word in the heading swaps for the next on a timer, the
 * outgoing word rising away as the incoming one arrives from below.
 *
 * Adapted from the supplied rotating-text reference, with two changes:
 *
 *  1. Words come from child elements rather than a comma-separated attribute,
 *     so a word containing a comma can't split into two.
 *
 *  2. No SplitText line-masking. The reference re-split the heading on resize
 *     (autoSplit), which would tear down and rebuild the very span being
 *     animated mid-flight. Clipping is done with CSS overflow on the word
 *     wrapper instead, which survives reflow and needs no re-init.
 *
 * The wrapper's width tweens between words so the following text slides along
 * with the change rather than jumping.
 *
 * Under prefers-reduced-motion the first word is left in place and no cycling
 * starts — the heading still reads correctly, it just doesn't move.
 *
 * No-ops when the markup isn't present.
 */
function initTextCycle() {
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  // Per-line panels for the static heading. Independent of the cycling below,
  // so it runs even on blocks with a single word or under reduced motion.
  initHighlightLines();

  document.querySelectorAll("[data-cycle-title]").forEach((heading) => {
    // Guard against double-init if called twice on the same DOM.
    if (heading.hasAttribute("data-cycle-initialized")) return;
    heading.setAttribute("data-cycle-initialized", "");

    const slot = heading.querySelector("[data-cycle-words]");
    if (!slot) return;

    const wordEls = Array.from(slot.querySelectorAll("[data-cycle-word]"));
    if (!wordEls.length) return;

    // NOTE: the server-rendered `hidden` attribute is NOT cleared here. Setup
    // is deferred until the webfont settles, and unhiding this early left every
    // word visible in normal flow during that gap — rendering them stacked on
    // top of each other. It is cleared inside the deferred block below, one
    // step before GSAP takes over positioning.

    // One word: nothing to cycle. Leave it as static text.
    if (wordEls.length === 1 || reduceMotion) {
      wordEls.forEach((el) => {
        el.hidden = false;
      });
      gsap.set(slot, { width: "auto" });
      return;
    }

    const stepDuration =
      parseFloat(heading.getAttribute("data-step-duration")) || 1.75;
    const inDuration = 0.75;
    const outDuration = 0.6;

    // Measuring must wait for the webfont. Anton is much narrower than the
    // fallback, so widths taken on DOMContentLoaded are wrong — and on a SOFT
    // refresh (fonts already cached) the timing shifted enough that the stack
    // was set up before layout settled and every word sat on top of the others.
    // A hard refresh happened to win the race, which is why it looked
    // intermittent. document.fonts.ready resolves immediately when fonts are
    // already loaded, so this is correct in both cases.
    const fontsReady =
      document.fonts && document.fonts.ready
        ? document.fonts.ready
        : Promise.resolve();

    // Measure every word up front so width tweens don't have to read layout
    // mid-animation (each word is absolutely positioned once stacked, so its
    // own box stays measurable).
    const widthOf = (el) => el.getBoundingClientRect().width;

    let activeIndex = 0;
    let timer;

    const showNext = () => {
      const nextIndex = (activeIndex + 1) % wordEls.length;
      const prev = wordEls[activeIndex];
      const current = wordEls[nextIndex];

      gsap.to(slot, {
        width: widthOf(current),
        duration: inDuration,
        ease: "power4.inOut",
      });

      gsap.to(prev, {
        yPercent: -150,
        autoAlpha: 0,
        duration: outDuration,
        ease: "power4.inOut",
      });

      gsap.fromTo(
        current,
        { yPercent: 150, autoAlpha: 0 },
        {
          yPercent: 0,
          autoAlpha: 1,
          duration: inDuration,
          ease: "power4.inOut",
        },
      );

      activeIndex = nextIndex;
      timer = gsap.delayedCall(stepDuration, showNext);
    };

    // Stack and start only once the font is settled — see fontsReady above.
    // Two rAFs: the first lets the font-swap reflow commit, the second measures
    // against that finished layout. One frame was not always enough.
    fontsReady.then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Unhide and stack in the SAME frame: `hidden` is display:none, so
          // the words can't be measured until it goes, but leaving them
          // visible for even one frame before GSAP positions them shows the
          // whole list stacked.
          wordEls.forEach((el) => {
            el.hidden = false;
          });

          // All but the active word sit below the clip, hidden.
          gsap.set(wordEls, { yPercent: 150, autoAlpha: 0 });
          gsap.set(wordEls[activeIndex], { yPercent: 0, autoAlpha: 1 });
          gsap.set(slot, { width: widthOf(wordEls[activeIndex]) });

          timer = gsap.delayedCall(stepDuration, showNext);
        });
      });
    });

    // Re-measure on resize: the active word's width changes with the font size,
    // and a stale inline width would leave a gap before the following text.
    let resizeTimer;
    window.addEventListener("resize", () => {
      resizeTimer && clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        gsap.set(slot, { width: widthOf(wordEls[activeIndex]) });
      }, 150);
    });

    // Pause while the block is off-screen so the timer isn't burning through
    // words nobody is watching — it resumes at the current word on return.
    //
    // `timer` is reassigned on every step, so the observer must act on whatever
    // the CURRENT call is, not the one captured when it was registered.
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!timer) return;
            if (entry.isIntersecting) {
              timer.play();
            } else {
              timer.pause();
            }
          });
        },
        { rootMargin: "0px" },
      ).observe(heading);
    }
  });
}

export { initTextCycle };
